import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ShoppingBag, User2, ChefHat, LayoutDashboard, MonitorPlay, LogOut, UtensilsCrossed, Truck, Award, Server, Boxes, Users, Building2, BarChart3, MessageSquareHeart, Megaphone, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export function PublicHeader() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 w-full border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <UtensilsCrossed className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight">City Grill</span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <NavLink data-testid="nav-menu-link" to="/menu" className={({ isActive }) => `text-sm font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-foreground/80'}`}>Menu</NavLink>
          <NavLink data-testid="nav-track-link" to="/orders" className={({ isActive }) => `text-sm font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-foreground/80'}`}>My Orders</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="nav-cart-button"
            variant="secondary"
            size="sm"
            onClick={() => navigate('/cart')}
            className="relative h-10 px-3"
          >
            <ShoppingBag className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <Badge data-testid="nav-cart-badge" className="ml-2 bg-primary text-primary-foreground">{count}</Badge>
            )}
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="nav-user-menu-button" variant="ghost" size="sm" className="h-10 gap-2">
                  <User2 className="h-4 w-4" />
                  <span className="hidden max-w-[120px] truncate sm:inline">{user.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user.full_name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="nav-my-orders-menu-item">
                  <ShoppingBag className="mr-2 h-4 w-4" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/loyalty')} data-testid="nav-loyalty-menu-item">
                  <Award className="mr-2 h-4 w-4" /> Loyalty & Rewards
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile-menu-item">
                  <User2 className="mr-2 h-4 w-4" /> Profile
                </DropdownMenuItem>
                {['admin', 'owner', 'cashier'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/server')} data-testid="nav-server-menu-item">
                    <Server className="mr-2 h-4 w-4" /> Server Console
                  </DropdownMenuItem>
                )}
                {['admin', 'owner', 'cashier'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-menu-item">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                {['admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/inventory')} data-testid="nav-inventory-menu-item">
                    <Boxes className="mr-2 h-4 w-4" /> Inventory
                  </DropdownMenuItem>
                )}
                {['admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/staff')} data-testid="nav-staff-menu-item">
                    <Users className="mr-2 h-4 w-4" /> Staff
                  </DropdownMenuItem>
                )}
                {['admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/branches-admin')} data-testid="nav-branches-menu-item">
                    <Building2 className="mr-2 h-4 w-4" /> Branches
                  </DropdownMenuItem>
                )}
                {['admin', 'owner', 'cashier'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/reports')} data-testid="nav-reports-menu-item">
                    <BarChart3 className="mr-2 h-4 w-4" /> Reports
                  </DropdownMenuItem>
                )}
                {['admin', 'owner', 'cashier'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/feedback-admin')} data-testid="nav-feedback-menu-item">
                    <MessageSquareHeart className="mr-2 h-4 w-4" /> Feedback
                  </DropdownMenuItem>
                )}
                {['admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/marketing')} data-testid="nav-marketing-menu-item">
                    <Megaphone className="mr-2 h-4 w-4" /> Marketing
                  </DropdownMenuItem>
                )}
                {['admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/audit')} data-testid="nav-audit-menu-item">
                    <ShieldAlert className="mr-2 h-4 w-4" /> Audit log
                  </DropdownMenuItem>
                )}
                {['kitchen', 'admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/kds')} data-testid="nav-kds-menu-item">
                    <ChefHat className="mr-2 h-4 w-4" /> Kitchen Display
                  </DropdownMenuItem>
                )}
                {['cashier', 'admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/pos')} data-testid="nav-pos-menu-item">
                    <MonitorPlay className="mr-2 h-4 w-4" /> POS
                  </DropdownMenuItem>
                )}
                {['driver', 'admin', 'owner'].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate('/driver')} data-testid="nav-driver-menu-item">
                    <Truck className="mr-2 h-4 w-4" /> Driver app
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); }} data-testid="nav-logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="nav-signin-button" variant="ghost" size="sm" onClick={() => navigate('/login')}>Sign in</Button>
              <Button data-testid="nav-signup-button" size="sm" onClick={() => navigate('/signup')}>Sign up</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t border-border bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <UtensilsCrossed className="h-4 w-4" />
              </span>
              <span className="font-display text-lg font-semibold">City Grill</span>
            </div>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">Live ordering, kitchen-grade reliability and a secure foundation. Built for restaurants that demand more.</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="rounded-full border border-border bg-secondary px-3 py-1">Secure checkout</span>
            <span className="rounded-full border border-border bg-secondary px-3 py-1">Live tracking</span>
            <span className="rounded-full border border-border bg-secondary px-3 py-1">No hidden fees</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
